/**
 * Bulk Quantity Modal
 *
 * Prompts user for quantity and optional color before adding a bulk item to the inquiry basket.
 */

import { useState, useCallback } from 'react';
import { Modal, TextField, Text, BlockStack } from '@shopify/polaris';
import { useInquiryStore } from '../../store/inquiryStore';
import { useUIStore } from '../../store/uiStore';

interface ProductPricing {
  id: string;
  quantityTier: string;
  unitPrice: number;
}

interface BulkQuantityModalProps {
  open: boolean;
  onClose: () => void;
  product: {
    id: string;
    name: string;
    slug: string;
    primaryImage: string | null;
    pricing: ProductPricing[];
  };
}

export function BulkQuantityModal({ open, onClose, product }: BulkQuantityModalProps) {
  const { addItem } = useInquiryStore();
  const { showToast } = useUIStore();

  // Default quantity: first pricing tier number or 20
  const defaultQty = product.pricing.length > 0
    ? parseInt(product.pricing[0].quantityTier.replace(/[^0-9]/g, ''), 10) || 20
    : 20;

  const [quantity, setQuantity] = useState(String(defaultQty));
  const [color, setColor] = useState('');

  const handleAdd = useCallback(() => {
    const qty = parseInt(quantity, 10);
    if (!qty || qty < 1) return;

    addItem({
      productId: product.id,
      productName: product.name,
      productSlug: product.slug,
      productImage: product.primaryImage,
      quantity: qty,
      color: color.trim() || null,
      type: 'BULK',
    });

    showToast('Added to inquiry basket', 'success');
    // Reset and close
    setQuantity(String(defaultQty));
    setColor('');
    onClose();
  }, [quantity, color, product, addItem, showToast, onClose, defaultQty]);

  const handleClose = useCallback(() => {
    setQuantity(String(defaultQty));
    setColor('');
    onClose();
  }, [onClose, defaultQty]);

  const pricingHint = product.pricing.length > 0
    ? `Bulk pricing starts at ${product.pricing[0].quantityTier} units`
    : 'Enter the quantity you need';

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={`Add "${product.name}" to Inquiry Basket`}
      primaryAction={{
        content: 'Add to Basket',
        onAction: handleAdd,
        disabled: !quantity || parseInt(quantity, 10) < 1,
      }}
      secondaryActions={[{ content: 'Cancel', onAction: handleClose }]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          <TextField
            label="Quantity"
            type="number"
            value={quantity}
            onChange={setQuantity}
            min={1}
            autoComplete="off"
            helpText={pricingHint}
          />
          <TextField
            label="Color / Variant (optional)"
            value={color}
            onChange={setColor}
            placeholder="e.g. Navy Blue, Red"
            autoComplete="off"
          />
          <Text as="p" variant="bodySm" tone="subdued">
            You can adjust quantity and add notes in the inquiry basket before submitting.
          </Text>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
